import React, { useState } from 'react';
import { TableDefault } from '../../shared/components/table/TableDefault';
import { TableColumnType } from '../../shared/components/table/table-types';
import { Avatar, Box, Card, Typography } from '@mui/material';
import {
  RolesOnUsers,
  useEasyDataUsersQuery,
  User,
  UserRoles,
  UserStatus,
} from '../../shared/types/generated';
import { SearchInput } from '../../shared/components/inputs/SearchInput';
import { getDateByTimezone, sanitizeUserStatus } from '../../shared/utils';
import { useTableSort } from '../../shared/components/table/useTableSort';
import { DEFAULT_TIMEZONE } from '../../shared/constants';
import { UpdateTenantUserDialog } from './components/UpdateTenantUserDialog';

type SettingUsersProps = {
  title?: string;
};

export const SettingUsers: React.FC<SettingUsersProps> = ({ title = 'Usuarios' }) => {
  const [updateUser, setUpdateUser] = useState<User | null>(null);
  const [count, setCount] = useState(0);
  const { sort, handleSort } = useTableSort();
  const [search, setSearch] = useState<string>();

  const [pagination, setPagination] = useState({ pageSize: 10, page: 1 });

  const { data, loading, refetch } = useEasyDataUsersQuery({
    fetchPolicy: 'network-only',
    variables: {
      where:
        search && search !== ''
          ? {
              OR: [
                {
                  name: {
                    startsWith: search,
                  },
                },
                {
                  email: {
                    startsWith: search,
                  },
                },
              ],
            }
          : {},
      orderBy: {
        [sort?.field || 'createdAt']: sort?.order,
      },
      skip: (pagination.page - 1) * pagination.pageSize,
      take: pagination.pageSize,
    },
    onCompleted: (event) => {
      setCount(event.easyDataUsers?.count ?? 0);
    },
  });

  const users = (data?.easyDataUsers?.items || []) as User[];

  const COLUMNS: TableColumnType<User>[] = [
    {
      columnName: '',
      columnValue: null,
      columnComponent: (item) => (
        <Box display="flex" justifyContent="center">
          <Avatar src={item?.avatar as string} sx={{ width: 24, height: 24 }} />
        </Box>
      ),
    },
    {
      columnName: 'NOMBRE',
      columnValue: (item) => item?.name || '',
      sortableField: 'name',
    },
    {
      columnName: 'EMAIL',
      columnValue: (item) => item?.email || '',
      sortableField: 'email',
    },
    {
      columnName: 'ROL',
      columnValue: (item) => {
        const rolesOnUsers = (item.roles_on_users as RolesOnUsers[]) || [];
        const role = rolesOnUsers.some(
          (role) => role?.role && role.role.name === UserRoles.Administrator,
        )
          ? 'Administrador'
          : 'Editor';
        return role;
      },
    },
    {
      columnName: 'ESTADO',
      columnValue: (item) => sanitizeUserStatus(item.user_status as UserStatus),
      sortableField: 'user_status',
    },
    {
      columnName: 'FECHA DE CREACIÓN',
      columnValue: (item) => getDateByTimezone(item.created_at as string, DEFAULT_TIMEZONE),
      sortableField: 'created_at',
    },
  ];

  return (
    <>
      <Box
        marginBottom={1}
        paddingX={1}
        display={{ xs: 'flex' }}
        flexDirection={{ xs: 'column', md: 'row' }}
        alignItems={'center'}
        justifyContent={'space-between'}
      >
        <Typography
          sx={{ textAlign: { xs: 'center', md: 'start' } }}
          fontSize={{ xs: '18px', md: '22px' }}
          fontWeight={700}
        >
          {title}
        </Typography>
        <SearchInput
          placeholder="BUSCAR USUARIO"
          sx={{ width: '100%', maxWidth: 400, marginBottom: '8px' }}
          onSearch={(value) => {
            setSearch(value);
          }}
        />
      </Box>
      <Card
        style={{
          border: '1px solid #BBBBBB',
          padding: '16px',
          borderRadius: '16px',
          boxShadow: '0px 0px 8px 0px #00000014',
        }}
      >
        <TableDefault
          count={count}
          items={users}
          columns={COLUMNS}
          page={pagination.page}
          pageSize={pagination.pageSize}
          loading={loading}
          messageLoading={'Cargando usuarios...'}
          actions={[
            {
              actionName: () => 'Actualizar',
              onClick: (item) => setUpdateUser(item),
            },
          ]}
          showPagination
          handleSort={handleSort}
          handleChangePage={(page) => {
            setPagination({
              ...pagination,
              page: page + 1,
            });
          }}
        />
        <UpdateTenantUserDialog
          open={!!updateUser}
          user={updateUser}
          onClose={() => {
            setUpdateUser(null);
          }}
          onSuccess={() => refetch()}
        />
      </Card>
    </>
  );
};
